import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Message } from '../../conversations/types/Conversation';
import { isOptimisticMessageId } from '../../conversations/utils/optimisticMessages';
import { WebWidgetClient } from '../services/webWidgetClient';
import { WebWidgetSessionProvider, useWebWidgetSession } from '../context/WebWidgetSessionContext';
import { WebWidgetTypingIndicator } from '../types/webWidget';
import { useWebWidgetMessageRailScroll } from '../hooks/useWebWidgetMessageRailScroll';
import { useWebWidgetNotificationSound } from '../hooks/useWebWidgetNotificationSound';
import { WebWidgetInteractiveMessage } from './WebWidgetInteractiveMessage';
import { WebWidgetMediaMessage } from './WebWidgetMediaMessage';
import {
  clearStoredWebWidgetIntro,
  readStoredWebWidgetIntro,
  writeStoredWebWidgetIntro,
} from '../utils/webWidgetSessionStorage';
import { isVisitorWebWidgetMessage } from '../utils/webWidgetMessageAlignment';
import styles from './EmbeddedWebWidget.module.css';

export type WebWidgetEmbedMode = 'demo' | 'production';

const formatTime = (value: string): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
};

const getSenderLabel = (
  senderType: string,
  senderName: string | undefined,
  embedMode: WebWidgetEmbedMode,
  t: (key: string) => string,
): string => {
  if (senderName?.trim()) {
    return senderName.trim();
  }

  switch (senderType) {
    case 'companyMember':
      return embedMode === 'production' ? t('senderSimaizeerTeam') : t('senderSupportTeam');
    case 'aiAssistant':
      return t('senderAssistant');
    case 'lead':
      return embedMode === 'production' ? t('senderYou') : t('senderLead');
    case 'system':
      return t('senderSystem');
    case 'customer':
    default:
      return t('senderYou');
  }
};

const getTypingLabel = (
  indicator: WebWidgetTypingIndicator,
  embedMode: WebWidgetEmbedMode,
  t: (key: string) => string,
): string => {
  if (indicator.displayName?.trim()) {
    return indicator.displayName.trim();
  }

  if (indicator.participantType === 'aiAssistant') {
    return t('senderAssistant');
  }

  return embedMode === 'production' ? t('senderSimaizeerTeam') : t('senderSupportTeam');
};

const isValidEmail = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
};

const isValidPhone = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
};

const meetsVisitorIdentityRequirements = (
  settings: { requireEmail: boolean; requirePhone: boolean },
  email: string,
  phone: string,
): boolean => {
  const emailValue = email.trim();
  const phoneValue = phone.trim();

  if (settings.requireEmail && !emailValue) {
    return false;
  }

  if (settings.requirePhone && !phoneValue) {
    return false;
  }

  if (emailValue && !isValidEmail(emailValue)) {
    return false;
  }

  if (phoneValue && !isValidPhone(phoneValue)) {
    return false;
  }

  return true;
};

const getIdentityValidationMessage = (
  settings: { requireEmail: boolean; requirePhone: boolean },
  email: string,
  phone: string,
  t: (key: string) => string,
): string | null => {
  if (settings.requireEmail && !email.trim()) {
    return t('validationEmailRequired');
  }

  if (settings.requirePhone && !phone.trim()) {
    return t('validationPhoneRequired');
  }

  if (email.trim() && !isValidEmail(email)) {
    return t('validationEmailInvalid');
  }

  if (phone.trim() && !isValidPhone(phone)) {
    return t('validationPhoneInvalid');
  }

  return null;
};

const FieldLabel: React.FC<{ label: string; required?: boolean }> = ({ label, required = false }) => (
  <span className={styles.label}>
    {label}
    {required ? <span className={styles.requiredStar} aria-hidden="true">*</span> : null}
  </span>
);

interface EmbeddedWebWidgetShellProps {
  tenantCode: string;
  widgetKey: string;
  autoOpen?: boolean;
  embedMode?: WebWidgetEmbedMode;
  launcherLabel?: string;
  defaultDisplayName?: string;
  defaultExternalVisitorId?: string;
  pollingIntervalMs?: number;
}

const EmbeddedWebWidgetShell: React.FC<EmbeddedWebWidgetShellProps> = ({
  tenantCode,
  widgetKey,
  autoOpen = false,
  embedMode = 'demo',
  launcherLabel,
  defaultDisplayName,
  defaultExternalVisitorId,
  pollingIntervalMs,
}) => {
  const { t } = useTranslation('webWidget');
  const isProductionEmbed = embedMode === 'production';
  const {
    appearance,
    conversation,
    error,
    hasMoreMessages,
    isReady,
    lastSyncedAt,
    loadMoreMessages,
    messages,
    nextPollAt,
    pollFailureCount,
    refreshSession,
    recover,
    revokeSession,
    sendAttachment,
    sendMessage,
    sessionId,
    sessionExpiresAtUtc,
    syncStatus,
    status,
    visibleTypingIndicators,
    visitor,
    visitorIdentity,
    welcomeMessage,
    welcomeTitle,
    widgetName,
  } = useWebWidgetSession();
  const [isOpen, setIsOpen] = useState(autoOpen);
  const [composer, setComposer] = useState('');
  const [displayName, setDisplayName] = useState(defaultDisplayName || '');
  const [externalVisitorId, setExternalVisitorId] = useState(defaultExternalVisitorId || '');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [introCompleted, setIntroCompleted] = useState(false);
  const { messageRailRef } = useWebWidgetMessageRailScroll({
    conversationId: conversation?.id,
    messages,
    typingIndicatorCount: visibleTypingIndicators.length,
  });
  useWebWidgetNotificationSound({
    messages,
    sessionId,
    isReady,
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const recoverRef = useRef(recover);

  useEffect(() => {
    const introDraft = readStoredWebWidgetIntro(tenantCode, widgetKey);
    if (!introDraft) {
      return;
    }

    if (introDraft.displayName) {
      setDisplayName(current => current || introDraft.displayName || '');
    }

    if (introDraft.externalVisitorId) {
      setExternalVisitorId(current => current || introDraft.externalVisitorId || '');
    }

    if (introDraft.email) {
      setEmail(current => current || introDraft.email || '');
    }

    if (introDraft.phone) {
      setPhone(current => current || introDraft.phone || '');
    }

    if (introDraft.introCompleted) {
      setIntroCompleted(true);
    }
  }, [tenantCode, widgetKey]);

  useEffect(() => {
    recoverRef.current = recover;
  }, [recover]);

  useEffect(() => {
    if (!isProductionEmbed) {
      return;
    }

    void recoverRef.current();
  }, [isProductionEmbed]);

  useEffect(() => {
    if (!isOpen || isProductionEmbed) {
      return;
    }

    void recoverRef.current();
  }, [isOpen, isProductionEmbed]);

  useEffect(() => {
    if (!visitor) {
      return;
    }

    setDisplayName(current => current || visitor.displayName || '');
    setExternalVisitorId(current => current || visitor.externalVisitorId || '');
    setEmail(current => current || visitor.email || '');
    setPhone(current => current || visitor.phone || '');
  }, [visitor]);

  useEffect(() => {
    if (conversation || messages.length > 0) {
      setIntroCompleted(true);
    }
  }, [conversation, messages.length]);

  useEffect(() => {
    if (!visitor) {
      return;
    }

    const hasSavedIdentity = Boolean(
      visitor.displayName?.trim()
      || visitor.email?.trim()
      || visitor.phone?.trim(),
    );

    if (hasSavedIdentity && meetsVisitorIdentityRequirements(visitorIdentity, visitor.email ?? '', visitor.phone ?? '')) {
      setIntroCompleted(true);
    }
  }, [visitor, visitorIdentity]);

  const isSessionBusy = status === 'initializing' || status === 'refreshing' || status === 'revoking';
  const showChatStep = introCompleted || !!conversation || messages.length > 0;
  const identityRequirementsMet = useMemo(
    () => meetsVisitorIdentityRequirements(visitorIdentity, email, phone),
    [email, phone, visitorIdentity],
  );
  const canSend = composer.trim().length > 0 && identityRequirementsMet && !isSessionBusy;
  const canAttach = identityRequirementsMet && !isSessionBusy;
  const canContinueIntro = isReady && identityRequirementsMet && !isSessionBusy;
  const identityValidationMessage = getIdentityValidationMessage(visitorIdentity, email, phone, t);
  const resolvedLauncherLabel = launcherLabel || appearance.launcherLabel || t('launcherDefault');
  const resolvedLauncherBrand = appearance.brandName || t('launcherBrand');
  const resolvedWelcomeTitle = welcomeTitle || widgetName || t('welcomeDefault');
  const resolvedWelcomeMessage = welcomeMessage || t('welcomeMessageDefault');

  const expiresText = useMemo(() => {
    if (!sessionExpiresAtUtc) {
      return null;
    }

    const parsed = new Date(sessionExpiresAtUtc);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(parsed);
  }, [sessionExpiresAtUtc]);

  const lastSyncedText = useMemo(() => {
    if (!lastSyncedAt) {
      return null;
    }

    const parsed = new Date(lastSyncedAt);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    }).format(parsed);
  }, [lastSyncedAt]);

  const pollingLabel = useMemo(() => {
    if (!pollingIntervalMs || pollingIntervalMs <= 0) {
      return t('pollingManual');
    }

    const seconds = pollingIntervalMs / 1000;
    return t('pollingEvery', {
      seconds: Number.isInteger(seconds) ? seconds : (seconds).toFixed(1),
    });
  }, [pollingIntervalMs, t]);

  const nextPollText = useMemo(() => {
    if (!nextPollAt) {
      return null;
    }

    const parsed = new Date(nextPollAt);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    }).format(parsed);
  }, [nextPollAt]);

  const syncBadgeLabel = useMemo(() => {
    if (syncStatus === 'degraded') {
      const retryTime = nextPollText || t('scheduled');
      return pollFailureCount > 1
        ? t('syncDegradedRetry', { time: retryTime })
        : t('syncDelayedRetry', { time: retryTime });
    }

    if (syncStatus === 'live') {
      return t('syncLive');
    }

    return t('syncIdle');
  }, [nextPollText, pollFailureCount, syncStatus, t]);

  const buildVisitorMessagePayload = useCallback((content: string) => ({
    content: content.trim(),
    displayName: displayName.trim() || undefined,
    externalVisitorId: externalVisitorId.trim() || undefined,
    email: email.trim() || undefined,
    phone: phone.trim() || undefined,
  }), [displayName, email, externalVisitorId, phone]);

  const handleIntroSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canContinueIntro) {
      return;
    }

    setIntroCompleted(true);
    writeStoredWebWidgetIntro(tenantCode, widgetKey, {
      introCompleted: true,
      displayName: displayName.trim() || undefined,
      externalVisitorId: externalVisitorId.trim() || undefined,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
    });
  };

  const submitComposerMessage = useCallback(async () => {
    if (!canSend) {
      return;
    }

    const payload = buildVisitorMessagePayload(composer);
    setComposer('');
    await sendMessage(payload);
  }, [buildVisitorMessagePayload, canSend, composer, sendMessage]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await submitComposerMessage();
  };

  const handleComposerKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }

    event.preventDefault();
    void submitComposerMessage();
  };

  const handleRevokeSession = async () => {
    await revokeSession();
    clearStoredWebWidgetIntro(tenantCode, widgetKey);
    setIntroCompleted(false);
    await recover();
  };

  const handleInteractiveSelection = useCallback(async (_message: Message, replyText: string) => {
    const trimmedReplyText = replyText.trim();
    if (!trimmedReplyText || isSessionBusy) {
      return;
    }

    await sendMessage(buildVisitorMessagePayload(trimmedReplyText));
  }, [buildVisitorMessagePayload, isSessionBusy, sendMessage]);

  const handleAttachmentSelected = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file || isSessionBusy || !identityRequirementsMet) {
      return;
    }

    const attachmentPayload = {
      file,
      content: composer.trim() || undefined,
      displayName: displayName.trim() || undefined,
      externalVisitorId: externalVisitorId.trim() || undefined,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
    };
    setComposer('');
    await sendAttachment(attachmentPayload);
  }, [composer, displayName, email, externalVisitorId, identityRequirementsMet, isSessionBusy, phone, sendAttachment]);

  const renderMessageContent = useCallback((message: Message) => {
    if (message.messageType === 'interactive' && (message.interactiveList || message.interactiveButtons)) {
      return (
        <WebWidgetInteractiveMessage
          message={message}
          disabled={isSessionBusy}
          onSelect={replyText => handleInteractiveSelection(message, replyText)}
        />
      );
    }

    if (message.mediaUrl && ['image', 'audio', 'video', 'document', 'sticker'].includes(message.messageType)) {
      return <WebWidgetMediaMessage message={message} />;
    }

    // Text messages can still embed image/video URLs (including Markdown) that should render inline.
    return <WebWidgetMediaMessage message={message} />;
  }, [handleInteractiveSelection, isSessionBusy]);

  const primaryColor = appearance.primaryColor?.trim() || (isProductionEmbed ? '#4F46E5' : '#0f766e');

  const shell = (
    <div
      className={styles.shell}
      data-embed={isProductionEmbed ? 'production' : 'demo'}
      style={{ ['--widget-primary' as any]: primaryColor }}
    >
      {!isOpen ? (
        <button type="button" className={styles.launcher} onClick={() => setIsOpen(true)}>
          <span className={styles.launcherGlyph} aria-hidden="true">{isProductionEmbed ? '💬' : '✦'}</span>
          <span className={styles.launcherText}>
            <span className={styles.launcherEyebrow}>{resolvedLauncherBrand}</span>
            <span className={styles.launcherLabel}>{resolvedLauncherLabel}</span>
          </span>
        </button>
      ) : (
        <section className={styles.panel} aria-label={resolvedLauncherLabel}>
          <header className={styles.header}>
            <div className={styles.headerTop}>
              <div className={styles.brand}>
                <div className={styles.brandMark} aria-hidden="true">{isProductionEmbed ? 'SZ' : 'BMS'}</div>
                <div className={styles.brandText}>
                  {!isProductionEmbed ? <span className={styles.eyebrow}>{t('hostedWidget')}</span> : null}
                  <h2 className={styles.title}>{resolvedWelcomeTitle}</h2>
                  <p className={styles.subtitle}>{resolvedWelcomeMessage}</p>
                </div>
              </div>
              <div className={styles.headerActions}>
                {!isProductionEmbed ? (
                  <button type="button" className={styles.iconButton} onClick={() => void refreshSession()} title={t('refreshSession')}>
                    ↻
                  </button>
                ) : null}
                <button type="button" className={styles.iconButton} onClick={() => setIsOpen(false)} title={t('minimize')}>
                  −
                </button>
              </div>
            </div>
            {!isProductionEmbed ? (
              <div className={styles.statusBar}>
                <span className={styles.statusPill}><span className={styles.pulse} aria-hidden="true" />{conversation ? t('conversationActive') : t('readyToStart')}</span>
                {expiresText ? <span className={styles.statusPill}>{t('sessionUntil', { value: expiresText })}</span> : null}
                <span className={styles.statusPill}>{pollingLabel}</span>
                <span className={`${styles.statusPill} ${syncStatus === 'degraded' ? styles.statusPillWarning : ''}`}>{syncBadgeLabel}</span>
                {lastSyncedText ? <span className={styles.statusPill}>{t('lastSynced', { value: lastSyncedText })}</span> : null}
              </div>
            ) : null}
          </header>

          <div className={styles.body}>
            {showChatStep ? (
              <>
                <div className={styles.messageRail} ref={messageRailRef}>
                  {error ? <div className={styles.errorCard}>{error}</div> : null}
                  {hasMoreMessages ? (
                    <button type="button" className={styles.loadMore} onClick={() => void loadMoreMessages()} disabled={status === 'initializing'}>
                      {t('loadEarlierMessages')}
                    </button>
                  ) : null}
                  {!isProductionEmbed ? (
                    <div className={styles.introCard}>
                      <h3 className={styles.introTitle}>{t('thread')}</h3>
                      <p className={styles.introText}>
                        {conversation
                          ? t('threadActive')
                          : t('threadEmpty')}
                      </p>
                    </div>
                  ) : null}
                  {messages.length === 0 ? (
                    <div className={styles.emptyState}>
                      <div className={styles.emptyPanel}>
                        <h3 className={styles.emptyTitle}>{t('startConversation')}</h3>
                        <p className={styles.emptyCopy}>
                          {isProductionEmbed
                            ? t('emptyProduction')
                            : t('emptyDemo')}
                        </p>
                      </div>
                    </div>
                  ) : messages.map(message => {
                    const isOutgoing = isVisitorWebWidgetMessage(message);
                    const isPendingMessage = isOutgoing && isOptimisticMessageId(message.id);
                    return (
                      <div
                        key={message.id}
                        data-message-side={isOutgoing ? 'visitor' : 'agent'}
                        className={`${styles.messageRow} ${isOutgoing ? styles.messageOutgoing : styles.messageIncoming}`}
                      >
                        <article className={`${styles.bubble} ${isOutgoing ? styles.outgoingBubble : styles.incomingBubble} ${isPendingMessage ? styles.pendingBubble : ''}`}>
                          <span className={styles.sender}>{getSenderLabel(message.sender.type, message.sender.name, embedMode, t)}</span>
                          {renderMessageContent(message)}
                          <div className={styles.meta}>
                            <span>{formatTime(message.timestamp)}</span>
                            {isPendingMessage ? <span>{t('sendingMessage')}</span> : null}
                            {!isProductionEmbed ? <span>{message.source}</span> : null}
                          </div>
                        </article>
                      </div>
                    );
                  })}
                  {visibleTypingIndicators.map(indicator => (
                    <div
                      key={`typing-${indicator.participantType}-${indicator.participantId ?? indicator.displayName ?? 'default'}`}
                      className={`${styles.messageRow} ${styles.messageIncoming}`}
                    >
                      <article className={`${styles.bubble} ${styles.incomingBubble} ${styles.typingBubble}`}>
                        <span className={styles.sender}>{getTypingLabel(indicator, embedMode, t)}</span>
                        <div className={styles.typingDots} aria-hidden="true">
                          <span className={styles.typingDot} />
                          <span className={styles.typingDot} />
                          <span className={styles.typingDot} />
                        </div>
                        <div className={styles.meta}>
                          <span>{t('typingMessage')}</span>
                        </div>
                      </article>
                    </div>
                  ))}
                </div>

                <form className={styles.composerWrap} onSubmit={handleSubmit}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className={styles.hiddenFileInput}
                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.zip,.xls,.xlsx,.ppt,.pptx"
                    onChange={event => void handleAttachmentSelected(event)}
                    disabled={!isReady || isSessionBusy}
                  />
                  {!isReady ? (
                    <div className={styles.hint}>{status === 'error' ? t('unableToInitialize') : t('preparingSession')}</div>
                  ) : null}
                  <label className={styles.field}>
                    {!isProductionEmbed ? <FieldLabel label={t('message')} /> : null}
                    <textarea
                      className={styles.textarea}
                      value={composer}
                      onChange={event => setComposer(event.target.value)}
                      onKeyDown={handleComposerKeyDown}
                      maxLength={4000}
                      rows={isProductionEmbed ? 2 : undefined}
                      placeholder={isProductionEmbed ? t('messagePlaceholderProduction') : t('messagePlaceholderDemo')}
                      aria-label={t('message')}
                    />
                  </label>
                  <div className={styles.composerFooter}>
                    {!isProductionEmbed ? (
                      <span className={styles.hint}>
                        {status === 'initializing' ? t('connecting') : t('messagesThroughSession')}
                      </span>
                    ) : status === 'initializing' ? (
                      <span className={styles.hint}>{t('connecting')}</span>
                    ) : null}
                    <div className={styles.composerActions}>
                      {!isProductionEmbed ? (
                        <button type="button" className={styles.iconButton} onClick={() => void handleRevokeSession()} title={t('resetSession')}>
                          ⟲
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={!isReady || !canAttach}
                        title={t('attachFile')}
                        aria-label={t('attachFile')}
                      >
                        📎
                      </button>
                      <button type="submit" className={styles.sendButton} disabled={!canSend}>
                        {t('sendMessage')}
                      </button>
                    </div>
                  </div>
                </form>
              </>
            ) : (
              <form className={`${styles.composerWrap} ${styles.introStep}`} onSubmit={handleIntroSubmit}>
                {!isReady ? (
                  <div className={styles.hint}>{status === 'error' ? t('unableToInitialize') : t('preparingSession')}</div>
                ) : (
                  <>
                    <div className={styles.introCard}>
                      <h3 className={styles.introTitle}>
                        {isProductionEmbed ? t('beforeWeChat') : t('tellUsWhoYouAre')}
                      </h3>
                      <p className={styles.introText}>
                        {isProductionEmbed
                          ? t('introProduction')
                          : t('introDemo')}
                      </p>
                    </div>
                    {isProductionEmbed ? (
                      <label className={styles.field}>
                        <FieldLabel label={t('yourName')} />
                        <input
                          className={styles.input}
                          value={displayName}
                          onChange={event => setDisplayName(event.target.value)}
                          maxLength={120}
                          placeholder={t('namePlaceholder')}
                          aria-label={t('yourName')}
                        />
                      </label>
                    ) : (
                      <div className={styles.identityGrid}>
                        <label className={styles.field}>
                          <FieldLabel label={t('displayName')} />
                          <input className={styles.input} value={displayName} onChange={event => setDisplayName(event.target.value)} maxLength={120} aria-label={t('displayName')} />
                        </label>
                        <label className={styles.field}>
                          <FieldLabel label={t('visitorReference')} />
                          <input className={styles.input} value={externalVisitorId} onChange={event => setExternalVisitorId(event.target.value)} maxLength={120} aria-label={t('visitorReference')} />
                        </label>
                      </div>
                    )}
                    <div className={styles.identityGrid}>
                      <label className={styles.field}>
                        <FieldLabel label={t('email')} required={visitorIdentity.requireEmail} />
                        <input
                          className={styles.input}
                          type="email"
                          value={email}
                          onChange={event => setEmail(event.target.value)}
                          maxLength={320}
                          placeholder={t('emailPlaceholder')}
                          required={visitorIdentity.requireEmail}
                          aria-required={visitorIdentity.requireEmail}
                          aria-label={t('email')}
                        />
                      </label>
                      <label className={styles.field}>
                        <FieldLabel label={t('phone')} required={visitorIdentity.requirePhone} />
                        <input
                          className={styles.input}
                          type="tel"
                          value={phone}
                          onChange={event => setPhone(event.target.value)}
                          maxLength={40}
                          placeholder={t('phonePlaceholder')}
                          required={visitorIdentity.requirePhone}
                          aria-required={visitorIdentity.requirePhone}
                          aria-label={t('phone')}
                        />
                      </label>
                    </div>
                    {identityValidationMessage && isReady ? (
                      <div className={styles.hint}>{identityValidationMessage}</div>
                    ) : null}
                    <div className={styles.introActions}>
                      <span className={styles.hint}>
                        {isProductionEmbed ? t('introHintProduction') : t('introHintDemo')}
                      </span>
                      <button type="submit" className={styles.sendButton} disabled={!canContinueIntro}>
                        {t('continueToChat')}
                      </button>
                    </div>
                  </>
                )}
              </form>
            )}
          </div>
        </section>
      )}
    </div>
  );

  if (typeof document === 'undefined') {
    return shell;
  }

  return createPortal(shell, document.body);
};

export interface EmbeddedWebWidgetProps extends EmbeddedWebWidgetShellProps {
  tenantCode: string;
  widgetKey: string;
  client?: WebWidgetClient;
  initialMessagePageSize?: number;
}

export const EmbeddedWebWidget: React.FC<EmbeddedWebWidgetProps> = ({
  tenantCode,
  widgetKey,
  client,
  initialMessagePageSize,
  autoOpen,
  embedMode,
  launcherLabel,
  defaultDisplayName,
  defaultExternalVisitorId,
  pollingIntervalMs,
}) => {
  return (
    <WebWidgetSessionProvider
      tenantCode={tenantCode}
      widgetKey={widgetKey}
      client={client}
      initialMessagePageSize={initialMessagePageSize}
      pollingIntervalMs={pollingIntervalMs}
      origin={typeof window !== 'undefined' ? window.location.origin : undefined}
    >
      <EmbeddedWebWidgetShell
        tenantCode={tenantCode}
        widgetKey={widgetKey}
        autoOpen={autoOpen}
        embedMode={embedMode}
        launcherLabel={launcherLabel}
        defaultDisplayName={defaultDisplayName}
        defaultExternalVisitorId={defaultExternalVisitorId}
        pollingIntervalMs={pollingIntervalMs}
      />
    </WebWidgetSessionProvider>
  );
};

export default EmbeddedWebWidget;
